import { useEffect, useState } from "preact/hooks";
import { ec2Cache, headVersion, selectedVersion } from "../../state/appState";
import { buildStacks, filterInstancesByStackIds, filterInstancesBySolution } from "./stackBuilder";
import { fetchDemand } from "../../api/demandClient";
import { fetchEc2Instances } from "../../api/ec2Client";
import { fetchVersionInfo } from "../../api/versionClient";
import { ENVIRONMENTS } from "../../config";
import { solutionRegexes } from "../../utils/solutionQuery";
import type { Stack } from "../../types";
import { InstancesTable } from "./InstancesTable";
import { QueueTable, type QueueRow } from "./QueueTable";
import { Spinner } from "../../components/Spinner";

export function DemandPage({ solution }: { solution: string }) {
  const [stacks, setStacks] = useState<Record<string, Stack> | null>(null);
  const [queueRows, setQueueRows] = useState<QueueRow[]>([]);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [selectedStackId, setSelectedStackId] = useState<string | null>(null);
  const [refreshingEc2, setRefreshingEc2] = useState(false);

  useEffect(() => {
    fetchVersionInfo("refs/heads/main")
      .then((data) => { headVersion.value = data ? { ...data, specifier: "refs/heads/main", platform: "ue5" } : null; })
      .catch((err) => console.error("Error fetching HEAD version info:", err));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStacks(null);
    setSelectedStackId(null);
    selectedVersion.value = null;

    const instances = filterInstancesBySolution(ec2Cache.value ?? [], solution);
    const builtStacks = buildStacks(instances);
    const regexes = solutionRegexes(solution);

    Promise.all(
      ENVIRONMENTS.map((env) =>
        fetchDemand(env)
          .then((data) => [env, data] as const)
          .catch((err) => {
            console.error(`Error fetching demand for ${env}:`, err);
            return [env, { stacks: [], queues: [] }] as const;
          }),
      ),
    ).then((results) => {
      if (cancelled) return;
      const queues: QueueRow[] = [];

      for (const [env, data] of results) {
        for (const regex of regexes) {
          for (const stackStatus of data.stacks.filter((s) => regex.test(s.solution))) {
            const stack = builtStacks[stackStatus.id];
            if (stack) {
              stack.status = stackStatus.status;
              stack.playerId = stackStatus.playerId ?? "";
              stack.clientIp = stackStatus.clientIp ?? "";
              stack.kioskId = stackStatus.kioskId ?? "";
            }
          }
          for (const queue of data.queues.filter((q) => regex.test(q.solution))) {
            for (const item of queue.items) {
              queues.push({ solution: queue.solution, environment: env, ...item });
            }
          }
        }
      }

      setStacks({ ...builtStacks });
      setQueueRows(queues);
      setFetchedAt(new Date().toISOString());
    });

    return () => {
      cancelled = true;
    };
  }, [solution, ec2Cache.value]);

  async function refreshEc2() {
    // Refresh exactly the stack ids on screen, rather than re-deriving a solution filter:
    // covers wildcard/prefix matches (e.g. "Danhostel" also matching "Danhostel-TEMP") and
    // multi-solution "a|b" searches without re-running that matching logic here.
    const ids = Object.keys(stacks ?? {});
    if (ids.length === 0) return;
    setRefreshingEc2(true);
    try {
      const fresh = await fetchEc2Instances(`ids:${ids.join(",")}`);
      const stale = new Set(filterInstancesByStackIds(ec2Cache.value ?? [], ids));
      ec2Cache.value = [...(ec2Cache.value ?? []).filter((i) => !stale.has(i)), ...fresh];
    } catch (err) {
      console.error(`Error refreshing EC2 instances for stacks in ${solution}:`, err);
    } finally {
      setRefreshingEc2(false);
    }
  }

  function selectStackVersion(stack: Stack) {
    setSelectedStackId(stack.id);
    let specifier = stack.versionOriginal ?? stack.version ?? "refs/heads/main";
    if (stack.version && /^(\d+(\.\d+){1,2})$/.test(stack.version)) {
      specifier = `v_${stack.solution}_${stack.version}`;
    }
    fetchVersionInfo(specifier, "ue5", solution)
      .then((data) => {
        selectedVersion.value = data
          ? { ...data, specifier, platform: "ue5", CloudFormationStackVersion: stack.stackVersion }
          : null;
      })
      .catch((err) => console.error("Error fetching selected version info:", err));
  }

  if (!stacks) return <Spinner label="Loading..." />;

  return (
    <div id="demandDiv">
      {fetchedAt && (
        <div id="fetchStatus">
          Data fetched: {fetchedAt}{" "}
          <span
            class="refresh-cache"
            title="Refresh EC2, instance status, queue, and version data for this solution"
            onClick={refreshingEc2 ? undefined : refreshEc2}
          >
            {refreshingEc2 ? <Spinner /> : "🔄"}
          </span>
        </div>
      )}
      <fieldset>
        <legend>
          <strong>Instances</strong>
        </legend>
        <InstancesTable stacks={stacks} selectedStackId={selectedStackId} onSelectVersion={selectStackVersion} />
      </fieldset>
      <fieldset>
        <legend>
          <strong>Queue</strong>
        </legend>
        <QueueTable rows={queueRows} />
      </fieldset>
    </div>
  );
}
